/*
  ActiveClient context — remembers the "client the therapist is currently
  working on" across screens.

  When the user opens a client's detail page, we stash their email + name.
  Any downstream screen (Letters compose, etc.) can then read this and
  offer a one-tap "back to <name>" link — no need to navigate to /clients
  and re-search every time.

  Persisted to localStorage so it survives full-page refreshes.
*/
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "acuity-reports:active-client";

function readInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.email) return parsed;
  } catch (e) { /* ignore */ }
  return null;
}

const ActiveClientCtx = createContext({
  client: null,
  setClient: () => {},
  clearClient: () => {},
});

export function ActiveClientProvider({ children }) {
  const [client, setClientState] = useState(readInitial);

  useEffect(() => {
    try {
      if (client) localStorage.setItem(STORAGE_KEY, JSON.stringify(client));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* ignore */ }
  }, [client]);

  const setClient = useCallback((next) => {
    if (!next || !next.email) return;
    setClientState({
      email: (next.email || "").toLowerCase(),
      name: next.name || "",
      canonical_client_id: next.canonical_client_id || null,
      updated_at: new Date().toISOString(),
    });
  }, []);

  const clearClient = useCallback(() => setClientState(null), []);

  return (
    <ActiveClientCtx.Provider value={{ client, setClient, clearClient }}>
      {children}
    </ActiveClientCtx.Provider>
  );
}

export function useActiveClient() {
  return useContext(ActiveClientCtx);
}
